import omit from "lodash/omit";
import { useMemo } from "react";
import { JsonDiffEditor, JsonEditor } from "@/common/JsonEditor";

// addEvent stores the request alongside the diff in `meta`; these keys are not part of the diff (see the Raw tab).
const REQUEST_KEYS = ["resource", "path", "method", "requestPath", "projectId", "releaseId"];

const MISSING = Symbol("missing");
type Side = unknown | typeof MISSING;

const isArrayDelta = (delta: any) => Boolean(delta) && typeof delta === "object" && !Array.isArray(delta) && delta._t === "a";

/**
 * The before and after sides of a jsondiffpatch delta (imminently_fn's getDiff): added `[new]`, changed
 * `[old, new]`, removed `[old, 0, 0]`, nested objects, and arrays as `{ _t: "a", <index>: delta }`. Only the changed
 * parts are included, since the event stores no full document. A plain value (such as the @id getContactDiff keeps on
 * a changed entity) is on both sides.
 */
export const deltaSides = (delta: any): { before: Side; after: Side } => {
  if (Array.isArray(delta)) {
    if (delta.length === 1) return { before: MISSING, after: delta[0] };
    if (delta.length === 2) return { before: delta[0], after: delta[1] };
    if (delta.length === 3 && delta[1] === 0 && delta[2] === 0) return { before: delta[0], after: MISSING };
    return { before: delta, after: delta };
  }
  if (!delta || typeof delta !== "object") return { before: delta, after: delta };
  if (isArrayDelta(delta)) {
    // "_<n>" is jsondiffpatch's key for an item removed from index n; getDiff uses "<n>" for every change.
    const entries = Object.entries(delta)
      .filter(([key]) => key !== "_t")
      .map(([key, value]) => ({ index: Number(key.replace(/^_/, "")), sides: deltaSides(value) }))
      .sort((a, b) => a.index - b.index);
    return {
      before: entries.map((entry) => entry.sides.before).filter((value) => value !== MISSING),
      after: entries.map((entry) => entry.sides.after).filter((value) => value !== MISSING),
    };
  }
  const before: Record<string, unknown> = {};
  const after: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(delta)) {
    const sides = deltaSides(value);
    if (sides.before !== MISSING) before[key] = sides.before;
    if (sides.after !== MISSING) after[key] = sides.after;
  }
  return { before, after };
};

const toJson = (value: Side) => (value === MISSING ? "" : JSON.stringify(value, null, 2));

/** An event's `meta` stored as a jsondiffpatch delta (metaFormat "diff"), shown as a Monaco diff of old and new values. */
export const EventMetaDiff = ({ meta }: { meta: Record<string, any> }) => {
  const { original, modified } = useMemo(() => {
    const { before, after } = deltaSides(omit(meta, REQUEST_KEYS));
    return { original: toJson(before), modified: toJson(after) };
  }, [meta]);

  if (!original && !modified) return <JsonEditor value={JSON.stringify(meta, null, 2)} readOnly withCopy />;
  return <JsonDiffEditor original={original} modified={modified} withCopy copyValue={JSON.stringify(meta, null, 2)} />;
};
